import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACKAGE_LABEL = { envoy: 'Gold Envoy $20', single: 'Single $190', couples: 'Couples $290' };

const AccountTab = ({ currentUser, setCurrentUser, appStatus, getAuthConfig, only }) => {
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/payments/me`, getAuthConfig())
      .then(res => setPayments(res.data || []))
      .catch(() => setPayments([]))
      .finally(() => setPaymentsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(null); setPasswordMessage(null);
    if (passwordForm.newPassword.length < 6) { setPasswordError('New password must be at least 6 characters'); return; }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { setPasswordError('New passwords do not match'); return; }
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword, new_password: passwordForm.newPassword
      }, getAuthConfig());
      setPasswordMessage('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    }
    setPasswordLoading(false);
  };

  const isPaid = (currentUser?.payment_status || 'unpaid') === 'paid';
  const show = (panel) => !only || only === panel;

  return (
    <div className="settings-section" data-testid="account-section">
      <div className="section-header">
        <h2>{only === 'settings' ? 'Notifications & Password' : 'Membership & Payment'}</h2>
        <p>{only === 'settings' ? 'How we contact you, and your login password' : 'Your membership status, fees and payment history'}</p>
      </div>
      <div style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Application Status */}
        {show('membership') && appStatus && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #2563eb' }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#1a2332' }}>Application Status</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                display: 'inline-block', padding: '4px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700',
                background: appStatus.status === 'approved' ? '#d1fae5' : appStatus.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                color: appStatus.status === 'approved' ? '#059669' : appStatus.status === 'rejected' ? '#dc2626' : '#d97706',
                textTransform: 'capitalize'
              }}>{appStatus.status || 'pending'}</span>
            </div>
            {appStatus.status === 'pending' && (
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '8px 0 0' }}>Your application is being reviewed. We'll notify you once a decision is made.</p>
            )}
          </div>
        )}

        {/* Payment & Billing */}
        {show('membership') && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${isPaid ? '#059669' : '#d97706'}` }} data-testid="payment-billing-card">
          <h3 style={{ margin: '0 0 12px 0', color: '#1a2332' }}>Payment &amp; Billing</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{
              display: 'inline-block', padding: '4px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700',
              background: isPaid ? '#d1fae5' : '#fef3c7', color: isPaid ? '#059669' : '#d97706'
            }} data-testid="payment-status-badge">{isPaid ? 'Paid' : 'Payment Required'}</span>
            {currentUser?.paid_package && <span style={{ fontSize: '13px', color: '#374151' }}>{PACKAGE_LABEL[currentUser.paid_package] || currentUser.paid_package}</span>}
          </div>
          {currentUser?.paid_at && (
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Paid on <strong style={{ color: '#1a2332' }}>{new Date(currentUser.paid_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
              {currentUser.paid_amount != null && <> &middot; <strong style={{ color: '#1a2332' }}>${Number(currentUser.paid_amount).toFixed(2)} AUD</strong></>}
            </div>
          )}
          {!isPaid && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', padding: '12px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '14px', color: '#92400e' }}>
              Your membership payment is pending. <a href="/payment" style={{ color: '#92400e', fontWeight: 600, textDecoration: 'underline' }} data-testid="pay-now-link">Pay now</a>.
            </div>
          )}
          <div style={{ marginTop: '12px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment History</h4>
            {paymentsLoading ? (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Loading…</div>
            ) : payments.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>No payments on file.</div>
            ) : (
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }} data-testid="payment-history-table">
                <thead>
                  <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
                    <th style={{ padding: '6px 0' }}>Date</th><th style={{ padding: '6px 0' }}>Package</th>
                    <th style={{ padding: '6px 0', textAlign: 'right' }}>Amount</th><th style={{ padding: '6px 0' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.session_id} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '8px 0', color: '#374151' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-AU') : '—'}</td>
                      <td style={{ padding: '8px 0', color: '#374151' }}>{PACKAGE_LABEL[p.package_id] || p.package_id}</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>${Number(p.amount || 0).toFixed(2)}</td>
                      <td style={{ padding: '8px 0', textTransform: 'capitalize', color: p.payment_status === 'paid' ? '#059669' : '#6b7280' }}>{p.payment_status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '12px 0 0' }}>Card details are stored securely by our payment providers (Stripe / PayPal). We never store your full card number.</p>
        </div>
        )}

        {/* Notification Preferences */}
        {show('settings') && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#1a2332' }}>Notification Preferences</h3>
          {[
            { key: 'push', label: 'In-App Notifications', desc: 'Bell icon alerts in the dashboard' },
            { key: 'email', label: 'Email Notifications', desc: 'Receive email for important updates' },
            { key: 'sms', label: 'SMS Notifications', desc: 'Text messages for urgent alerts (coming soon)' }
          ].map(({ key, label, desc }) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a2332' }}>{label}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{desc}</div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                <input type="checkbox" checked={currentUser?.notification_prefs?.[key] ?? (key !== 'sms')}
                  onChange={async (e) => {
                    const newPrefs = { ...(currentUser?.notification_prefs || { email: true, sms: false, push: true }), [key]: e.target.checked };
                    try { await axios.patch(`${API}/users/profile`, { notification_prefs: newPrefs }, getAuthConfig()); setCurrentUser(prev => ({ ...prev, notification_prefs: newPrefs })); } catch (err) { console.error('Notification prefs update failed:', err); }
                  }}
                  style={{ opacity: 0, width: 0, height: 0 }} data-testid={`notif-toggle-${key}`} />
                <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                  background: (currentUser?.notification_prefs?.[key] ?? (key !== 'sms')) ? '#2563eb' : '#d1d5db',
                  borderRadius: '12px', transition: 'background 0.2s' }}>
                  <span style={{ position: 'absolute', height: '18px', width: '18px', left: (currentUser?.notification_prefs?.[key] ?? (key !== 'sms')) ? '22px' : '3px',
                    bottom: '3px', background: 'white', borderRadius: '50%', transition: 'left 0.2s' }} />
                </span>
              </label>
            </div>
          ))}
        </div>
        )}

        {/* Change Password */}
        {show('settings') && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#1a2332' }}>Change Password</h3>
          {passwordMessage && <div style={{ background: '#d1fae5', border: '1px solid #059669', borderRadius: '8px', padding: '12px', color: '#065f46', marginBottom: '16px' }} data-testid="password-success-msg">{passwordMessage}</div>}
          {passwordError && <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '8px', padding: '12px', color: '#b91c1c', marginBottom: '16px' }} data-testid="password-error-msg">{passwordError}</div>}
          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Current Password</label>
              <input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})} required
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} data-testid="current-password-input" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>New Password</label>
              <input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})} required minLength={6}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} data-testid="new-password-input" />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Confirm New Password</label>
              <input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})} required minLength={6}
                style={{ width: '100%', padding: '10px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} data-testid="confirm-new-password-input" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={passwordLoading} style={{ padding: '12px 24px' }} data-testid="change-password-submit">
              {passwordLoading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
        )}
      </div>
    </div>
  );
};

export default AccountTab;
